import { cn } from "@/lib/utils/index";
import { AlertCircle, AlertTriangle, CheckCircle, Info, Sparkles } from "lucide-react";

type CalloutVariant = "info" | "warning" | "error" | "success" | "new" | "neutral";

interface FetchCalloutProps {
  variant?: CalloutVariant;
  children: React.ReactNode;
  className?: string;
  /** When true, renders as a clickable button instead of a static div */
  onClick?: () => void;
  /** Optional action element (button, link) rendered on the right side of the callout */
  action?: React.ReactNode;
}

const variantStyles: Record<CalloutVariant, { bg: string; text: string; icon: string; border: string }> = {
  info: {
    bg: "bg-blue-50",
    text: "text-blue-700",
    icon: "text-blue-500",
    border: "border-blue-200",
  },
  warning: {
    bg: "bg-yellow-50",
    text: "text-yellow-700",
    icon: "text-yellow-500",
    border: "border-yellow-200",
  },
  error: {
    bg: "bg-red-50",
    text: "text-red-700",
    icon: "text-red-500",
    border: "border-red-200",
  },
  success: {
    bg: "bg-green-50",
    text: "text-green-700",
    icon: "text-green-500",
    border: "border-green-200",
  },
  new: {
    bg: "bg-linear-to-br from-indigo-50 via-purple-50 to-pink-50",
    text: "text-gray-900",
    icon: "text-indigo-500",
    border: "border-indigo-200",
  },
  neutral: {
    bg: "bg-gray-50",
    text: "text-gray-600",
    icon: "text-gray-400",
    border: "border-gray-200",
  },
};

const variantIcons: Record<CalloutVariant, React.ElementType> = {
  info: Info,
  warning: AlertTriangle,
  error: AlertCircle,
  success: CheckCircle,
  new: Sparkles,
  neutral: Info,
};

/**
 * Reusable callout component for displaying contextual messages
 * Variants: info (blue), warning (yellow), error (red), success (green), new (gradient with NEW badge), neutral (gray)
 * @param variant - The visual style variant
 * @param children - The message content to display
 * @param className - Additional CSS classes to apply
 * @param onClick - Optional click handler (renders as button when provided)
 * @param action - Optional action element rendered on the right side
 */
export function FetchCallout({ variant = "info", children, className, onClick, action }: FetchCalloutProps) {
  const styles = variantStyles[variant];
  const Icon = variantIcons[variant];
  const isNew = variant === "new";

  const content = (
    <>
      <Icon className={cn("mt-0.5 h-4 w-4 shrink-0", styles.icon)} />
      <div className={cn("flex min-w-0 flex-1 flex-wrap items-center gap-2 text-sm font-medium", styles.text)}>
        <div className="min-w-0 max-w-full">{children}</div>
        {isNew && (
          <span className="rounded-full bg-linear-to-r from-indigo-500 to-purple-500 px-1.5 py-0.5 text-[10px] font-bold text-white">
            NEW
          </span>
        )}
      </div>
      {action}
    </>
  );

  const sharedClasses = cn(
    "flex min-w-0 max-w-full items-start gap-2 rounded-lg border p-3 text-left",
    styles.bg,
    styles.border,
    onClick && "cursor-pointer transition-all hover:shadow-sm",
    isNew && onClick && "hover:border-indigo-300",
    className
  );

  if (onClick) {
    return (
      <button type="button" onClick={onClick} className={cn(sharedClasses, "w-full")}>
        {content}
      </button>
    );
  }

  return <div className={sharedClasses}>{content}</div>;
}
